import React from "react";
import { Action, Dispatch } from "redux";
import { GetArtefactDetails } from "@emisgroup/cohort-builder-kit";
import { Tag } from "@emisgroup/ui-tag";
import { ExtractListSlice } from "../../../LeftPanel/ExtractList";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { TableDataRowProps } from "../../../Common/UI/Index";
import { VersionListType } from "../CreateVersion/redux/Types";
import { handleException } from "../../../../ErrorMessages/helper";
import { OptOutSlice } from "../../OptOut/redux/Index";
import { ParentOptOutModes } from "../../OptOut/redux/Types";
import { ExportStatus, LATESTVERSION } from "./Constant";
import "./Style.scss";

/**
 * Returns the latest version to add tag
 * @param extractVersion - Current version
 * @returns Tag with latest label
 */
const getextractVersion = (extractVersion: string): JSX.Element => {
  return (
    <div className="ebt-version-name">
      <div className="ebt-version-name__text-overflow">{extractVersion}</div>
      <div className="ebt-version-name_badge">
        <Tag
          id="ebt-version-latestTag"
          color="--primary-40"
          textColor="--neutral-95"
        >
          {LATESTVERSION}
        </Tag>
      </div>
    </div>
  );
};

/**
 * Converts date of string type to Date type
 * @param date - Contains the selected date
 * @returns - Returns the date by removing the time details
 */
export const convertDateToView = (date: string): Date => {
  const splittedDate = date.split(" ");
  if (splittedDate.length > 0) {
    const formatDate = splittedDate[0];
    date = formatDate.split("-").reverse().join("-");
  }
  const result = date.split("-").reverse().join("-");
  return new Date(result);
};

/**
 * Converts Date object to string in dd-mm-yyyy format
 * @param date - Contains the date to be formatted
 * @param isMonthInAphabet - Flag value to format the month type
 * @returns - Returns the dd-mm-yy format as string
 */
export const getFormattedDate = (date: Date, isMonthInAphabet: boolean): string => {
  const monthType = (isMonthInAphabet) ? "long" : "2-digit";
  const day = date?.toLocaleString("default", { day: "2-digit" });
  const month = date?.toLocaleString("default", { month: monthType });
  const year = date?.toLocaleString("default", { year: "numeric" });
  return day + "-" + month + "-" + year;
};

/**
 * Method to handle styling with respect to the status
 * @param statusType Banding status
 * @returns JSX element
 */
const getStatus = (statusType: string): JSX.Element => {
  const formattedStatus = statusType?.replace(/ /g, "")?.replace(/_/g, "");
  return <div className={`ebt-version-status__${formattedStatus}`}>{formattedStatus}</div>;
};

/**
 * Method for populating the version content in the table
 * @param versionList - version list to populate in table
 * @param currentVersion - current version to compare latest version
 * @returns the formatted data to populate the table
 */
export const populateTableData = (
  versionList: VersionListType[]
): TableDataRowProps[] => {
  // To remove the draft versions from the list
  const nonDraftVersions = versionList.filter((versions: VersionListType) => {
    return versions.name !== ExportStatus.DRAFT;
  });

  return nonDraftVersions.map((item: VersionListType) => {
    const { isCurrentVersion, name } = item;
    const currentName = isCurrentVersion ? getextractVersion(name) : name;
    const newDate = item.createdOn ? convertDateToView(item.createdOn) : "-";
    const formattedDate = (newDate && newDate !== "-") ? getFormattedDate(newDate, true) : newDate;
    const validDate = item.modifiedOn ? convertDateToView(item.modifiedOn) : "-";
    const validatedDate = (validDate && validDate !== "-") ? getFormattedDate(validDate, true) : validDate;
    // Below code can be reused while implementing export API
    // Const actions = <VersionActionIcons currentVersion={item.name} versionList={item} />;
    const versionRow: TableDataRowProps = {
      row: [
        {
          value: currentName ? currentName : "-",
          propertyName: "extractVersion"
        },
        {
          value: item.description ? item.description : "-",
          propertyName: "description"
        },
        {
          value: formattedDate,
          propertyName: "createdOn"
        },
        {
          value: item.createdBy ? item.createdBy : "-",
          propertyName: "createdBy"
        },
        {
          value: validatedDate,
          propertyName: "modifiedOn"
        },
        {
          value: item.modifiedBy ? item.createdBy : "-",
          propertyName: "modifiedBy"
        },
        {
          value: getStatus(item.status),
          propertyName: "status"
        }
      ]
    };
    return versionRow;
  });
};

/**
 * To convert the new api response to old api response type
 * @param newResponse new response
 * @returns the old response
 */
const convertToOldApiResponse = (newResponse: any): any => {
  const oldApiResponse = {
    data: {
      attributes: {
        extractGuid: newResponse.data.payload.id,
        extractName: newResponse.data.payload.name,
        createdOn: newResponse.data.payload.createdOn,
        createdBy: newResponse.data.payload.createdBy,
        lastModified: newResponse.data.payload.modifiedOn,
        metaData: newResponse.data.payload.metaData,
        financialYear: newResponse.data.payload.partition?.name,
        description: newResponse.data.payload.description,
        // Below fields are not available in the new response
        status: "",
        tag: "",
        isActive: true,
        isPublished: false,
        lastRunDate: new Date(),
        extractSchemaVersion: "",
        surveyName: "",
        currentVersion: "",
        lastExecutionInstanceId: ""
      },
    },
  };

  return oldApiResponse;
};

/**
 * Method for fetching the extract detail
 * @param dispatch - dispatches the events
 * @param selectedId - currently selected extract Guid
 * @param translate - translate object
 * @param token - access token
 */
export const GetSelectedExtractDetail = async (
  dispatch: Dispatch<Action>,
  selectedId: string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  translate: Function,
  token: string
): Promise<any> => {
  try {
    dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.ExtractDetails")));
    const newResponse = await GetArtefactDetails(
      selectedId,
      process.env.APP_TARGET_ENV as any,
      token,
      true
    );
    if (newResponse && newResponse["data"] && newResponse["data"]["payload"]) {
      const response = convertToOldApiResponse(newResponse);
      const currentExtract = response["data"]["attributes"];
      const currentMetaData = response["data"]["attributes"]["metaData"];
      const optOutMetaData = currentMetaData?.filter((item: any) => item.key === "optOuts");
      const optOutValues = optOutMetaData?.[0]?.value?.selection?.explorer_ipcv_vanilla;
      dispatch(ExtractListSlice.actions.selectedExtract(currentExtract));
      dispatch(OptOutSlice.actions.setOptOutList(optOutValues || []));
      dispatch(OptOutSlice.actions.setOptOutMode(
        !optOutValues ? ParentOptOutModes.CREATE : ParentOptOutModes.VIEW)
      );
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      return currentExtract;
    }
  } catch (ex) {
    handleException(ex, dispatch, "ExtractDetailErrMsg");
  }
};
